import { type MouseEvent, memo, useCallback } from 'react';

import { Button } from '@air/primitive-button';
import { cn } from '@air/utils-tailwind';

import {
  type GuidedVariantEditId,
  type GuidedVariantEditOption,
  isGuidedVariantEditId,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentGuidedVariantEdits';

export type WorkspaceAgentGuidedVariantChoicesProps = {
  onCancel: () => void;
  onSelect: (optionId: GuidedVariantEditId) => void;
  options: readonly GuidedVariantEditOption[];
};

export const WorkspaceAgentGuidedVariantChoices = memo(
  ({ onCancel, onSelect, options }: WorkspaceAgentGuidedVariantChoicesProps) => {
    const handleOptionClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        const optionId = event.currentTarget.dataset.optionId;

        if (optionId && isGuidedVariantEditId(optionId)) {
          onSelect(optionId);
        }
      },
      [onSelect],
    );

    return (
      <div className="flex flex-col gap-2" data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_CHOOSER">
        <div className="grid grid-cols-2 gap-2">
          {options.map((option) => {
            const Icon = option.icon;

            return (
              <button
                className={cn(
                  'flex min-w-0 rounded-16 border ads:border-ads-gray-5 border-gray-5 ads:bg-base bg-gray-1 text-left transition-colors duration-150 ease-out ads:hover:border-strong hover:border-gray-6 ads:hover:bg-raised hover:bg-gray-2 focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-9 active:border-gray-6 ads:active:border-strong active:bg-gray-2 ads:active:bg-raised',
                  option.hint ? 'flex-col items-start gap-1.5 p-3' : 'items-center gap-1 p-2',
                )}
                data-option-id={option.id}
                data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_OPTION"
                key={option.id}
                onClick={handleOptionClick}
                type="button"
              >
                <Icon aria-hidden className="shrink-0 ads:text-icon-emphasis text-gray-12" size="sm" />
                {option.hint ? (
                  <>
                    <span className="font-medium ads:text-primary text-14 text-gray-12 leading-5">{option.label}</span>
                    <span className="ads:text-metadata text-12 text-gray-9 leading-4">{option.hint}</span>
                  </>
                ) : (
                  <span className="min-w-0 truncate pr-1 font-medium ads:text-primary text-12 text-gray-12">
                    {option.label}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <Button
          appearance="ghost"
          className="self-start focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-9"
          color="gray"
          data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_CANCEL"
          onClick={onCancel}
          size="small"
        >
          Cancel
        </Button>
      </div>
    );
  },
);

WorkspaceAgentGuidedVariantChoices.displayName = 'WorkspaceAgentGuidedVariantChoices';
